import { parseArtifacts } from 'components/agent-interface/agent-workspace/utils/artifact-parser';
import { extractMessageContent } from './message-content-extractor';
import { isUserMessage, isSegmentQueryMessage, isQCHandoffToken, isSegmentAnalystHandoffToken, isSQLEngineerHandoff, isJourneyBuilderHandoff, isToolCallEvent, isHTMLTemplateEngineerHandoff, isStreamingChunkEvent, isHTMLTemplateMessage, isTemplateAnalystHandoffToken, isUserReviewHandoff } from './message-type-utils';

/**
 * Factory for creating agent message handlers
 * @param {object} params - Handler dependencies
 * @returns {object} Message handlers
 */
export const createMessageHandlers = ({
  showConsoleMessagesRef,
  setSegmentQueries,
  setHandoffTokenData,
  setTemplateData,
  setStreamingMessages,
  setMessages,
  setIsAgentTyping,
  mostRecentSegmentQueryIdRef,
  mostRecentTemplateDataIdRef,
  tokenBufferRef,
  bufferTimerRef,
  setCurrentWindowStateRef,
  setWorkspaceModeCallback,
  setJourneyActions,
  journeyActions
}) => {
  const logDebug = (message, ...args) => {
    if (showConsoleMessagesRef?.current) {
      console.log(message, ...args);
    }
  };

  /**
   * Handle segment query messages
   */
  const handleSegmentQuery = data => {
    logDebug('[AGENT_MESSAGE]: Captured SegmentQuery, storing content with ID:', data.id);
    mostRecentSegmentQueryIdRef.current = data.id;
    setSegmentQueries(prev => {
      const newMap = new Map(prev);
      newMap.set(data.id, data.content);
      return newMap;
    });
  };

  /**
   * Handle segment analyst handoff token
   */
  const handleSegmentAnalystHandoff = data => {
    logDebug('[AGENT_MESSAGE]: Captured SegmentAnalystHandoffToken with token:', data?.metadata?.handoff_token);
    const recentQueryId = mostRecentSegmentQueryIdRef.current;
    if (recentQueryId) {
      setHandoffTokenData(prev => {
        const newMap = new Map(prev);
        newMap.set(data.id, {
          type: 'segment',
          dataMessageId: recentQueryId
        });
        return newMap;
      });
      logDebug('[AGENT_MESSAGE]: Linked handoff token', data.id, 'to segment query', recentQueryId);
    }
  };

  /**
   * Handle scoping handoff tokens
   */
  const handleScopingHandoff = data => {
    if (isSQLEngineerHandoff(data)) {
      logDebug('[AGENT_MESSAGE]: Captured SQL_ENGINEER ScopingHandoffToken, ignoring message display');
      return true; // Signal to hide message
    }
    if (isJourneyBuilderHandoff(data)) {
      logDebug('[AGENT_MESSAGE]: JOURNEY_BUILDER handoff - maximizing window and switching to preview mode');
      if (setCurrentWindowStateRef.current) {
        setCurrentWindowStateRef.current('maximized');
      }
      setWorkspaceModeCallback('preview');
    }
    if (isHTMLTemplateEngineerHandoff(data)) {
      logDebug('[AGENT_MESSAGE]: Captured ScopingHandoffToken for HTML_TEMPLATE_ENGINEER, ignoring message display');
      return true; // Signal to hide message
    }
    return false;
  };

  /**
   * Handle HTML template messages
   */
  const handleHTMLTemplate = data => {
    logDebug('[AGENT_MESSAGE]: Captured HTMLTemplate, storing content with ID:', data.id);
    mostRecentTemplateDataIdRef.current = data.id;
    setTemplateData(prev => {
      const newMap = new Map(prev);
      newMap.set(data.id, data.content);
      return newMap;
    });
  };

  /**
   * Handle template analyst handoff token
   */
  const handleTemplateAnalystHandoff = data => {
    logDebug('[AGENT_MESSAGE]: Captured TemplateAnalystHandoffToken with token:', data?.metadata?.handoff_token);
    const recentTemplateId = mostRecentTemplateDataIdRef.current;
    if (recentTemplateId) {
      setHandoffTokenData(prev => {
        const newMap = new Map(prev);
        newMap.set(data.id, {
          type: 'template',
          dataMessageId: recentTemplateId
        });
        return newMap;
      });
      logDebug('[AGENT_MESSAGE]: Linked handoff token', data.id, 'to template data', recentTemplateId);
    }

    // Only display if USER_REVIEW
    return !isUserReviewHandoff(data, 'template');
  };

  /**
   * Handle journey builder messages
   */
  const handleJourneyBuilder = data => {
    logDebug('[AGENT_MESSAGE]: Captured JourneyBuilder, storing content with ID:', data.id);
    mostRecentJourneyDataIdRef.current = data.id;
    setJourneyData(prev => {
      const newMap = new Map(prev);
      newMap.set(data.id, data.content);
      return newMap;
    });
  };

  /**
   * Handle journey builder handoff token
   */
  const handleJourneyBuilderHandoff = data => {
    logDebug('[AGENT_MESSAGE]: Captured JourneyBuilderHandoffToken with token:', data?.metadata?.handoff_token);
    const recentJourneyId = mostRecentJourneyDataIdRef.current;
    if (recentJourneyId) {
      setHandoffTokenData(prev => {
        const newMap = new Map(prev);
        newMap.set(data.id, {
          type: 'journey',
          dataMessageId: recentJourneyId
        });
        return newMap;
      });
      logDebug('[AGENT_MESSAGE]: Linked handoff token', data.id, 'to journey data', recentJourneyId);
    }

    // Only display if USER_REVIEW
    return !isUserReviewHandoff(data, 'journey');
  };

  /**
   * Process and add message to state
   */
  const processAndAddMessage = data => {
    logDebug('Processing message - type:', data.type, 'source:', data.source);
    const displayContent = extractMessageContent(data);
    const {
      artifacts: parsedArtifacts,
      processedContent
    } = parseArtifacts(displayContent, showConsoleMessagesRef);
    if (parsedArtifacts.length > 0) {
      logDebug('[AGENT_MESSAGE]: Parsed', parsedArtifacts.length, 'artifacts at completion');
    }

    // Clean up token buffers
    if (tokenBufferRef.current.has(data.id)) {
      tokenBufferRef.current.delete(data.id);
    }
    if (bufferTimerRef.current.has(data.id)) {
      clearTimeout(bufferTimerRef.current.get(data.id));
      bufferTimerRef.current.delete(data.id);
    }

    // Remove from streaming
    setStreamingMessages(prev => {
      const newMap = new Map(prev);
      newMap.delete(data.id);
      return newMap;
    });
    const newMessage = {
      id: data.id,
      source: data.source,
      content: displayContent,
      processedContent: processedContent,
      rawContent: data.content,
      rawType: data.type,
      created_at: data.created_at,
      metadata: data.metadata,
      models_usage: data.models_usage,
      type: 'agent'
    };
    logDebug('Adding message to state:', newMessage);
    setMessages(prev => {
      const updated = [...prev, newMessage];
      logDebug('Updated messages array:', updated.length);
      return updated;
    });
    setIsAgentTyping(false);
  };

  /**
   * Main agent message handler
   */
  const handleAgentMessage = rawData => {
    logDebug('handleAgentMessage called with:', rawData);
    let data;
    try {
      data = typeof rawData === 'string' ? JSON.parse(rawData) : rawData;
      logDebug('Parsed data:', data);
    } catch (error) {
      if (showConsoleMessagesRef?.current) {
        console.error('Failed to parse agent message:', error);
      }
      return;
    }

    // Skip user echo messages
    if (isUserMessage(data)) {
      logDebug('Skipping user echo message');
      return;
    }

    // Handle segment query
    if (isSegmentQueryMessage(data)) {
      handleSegmentQuery(data);
      return;
    }

    // Handle QC handoff token
    if (isQCHandoffToken(data)) {
      logDebug('[AGENT_MESSAGE]: Captured QCHandoffToken, ignoring message display');
      return;
    }

    // Handle segment analyst handoff
    if (isSegmentAnalystHandoffToken(data)) {
      handleSegmentAnalystHandoff(data);
      // Don't return - let message be displayed if USER_REVIEW
    }

    // Handle scoping handoff
    if (handleScopingHandoff(data)) {
      return; // Message should be hidden
    }

    // Handle tool call events
    if (isToolCallEvent(data)) {
      logDebug(`[AGENT_MESSAGE]: Captured ${data.type}, ignoring message display`);
      return;
    }

    // Handle streaming chunk events
    if (isStreamingChunkEvent(data) && data?.source !== 'user') {
      logDebug('[AGENT_MESSAGE]: Captured ModelClientStreamingChunkEvent, ignoring message display');
      return;
    }

    // Handle HTML template
    if (isHTMLTemplateMessage(data)) {
      handleHTMLTemplate(data);
      return;
    }

    // Handle template analyst handoff
    if (isTemplateAnalystHandoffToken(data)) {
      if (handleTemplateAnalystHandoff(data)) {
        return; // Hide message
      }
    }

    // Process and add message if it has required fields
    if (data && data.source && data.id) {
      processAndAddMessage(data);
    } else {
      logDebug('Message missing required fields (source, id) - skipping');
    }
  };

  /**
   * Handle journey action messages
   */
  const handleAgentActionMessage = (data, callback) => {
    logDebug('[AGENT_MESSAGE]: Captured journey action:', data);
    console.log('[AGENT_MESSAGE]: Adding journey action to queue:', {
      ...data
    });
    setJourneyActions(prev => [...prev, {
      ...data,
      eventType: data.action,
      timestamp: Date.now(),
      callback: callback
    }]);
    console.log('[AGENT_MESSAGE]: Current journey actions queue:', journeyActions);
  };

  /**
   * Handle typing indicator
   */
  const handleAgentTyping = data => {
    logDebug('Agent: Typing indicator', data);
    setIsAgentTyping(data.isTyping);
  };

  /**
   * Handle agent errors
   */
  const handleAgentError = data => {
    if (showConsoleMessagesRef?.current) {
      console.error('Agent: Received error', data);
    }
    setMessages(prev => [...prev, {
      id: Date.now(),
      type: 'error',
      message: data.error || 'An error occurred',
      timestamp: Date.now()
    }]);
    setIsAgentTyping(false);
  };

  /**
   * Handle user input requests
   */
  const handleUserInputRequest = data => {
    logDebug('Agent: Received USER_INPUT_REQUEST', data);
  };

  /**
   * Flush buffered tokens to streaming messages
   */
  const flushTokenBuffer = messageId => {
    const buffer = tokenBufferRef.current.get(messageId);
    if (!buffer || buffer.length === 0) return;
    logDebug('[Token]: Flushing', buffer.length, 'tokens for message:', messageId);
    const combinedContent = buffer.map(t => t.content).join('');
    const firstToken = buffer[0];
    setStreamingMessages(prev => {
      const newMap = new Map(prev);
      const existing = newMap.get(messageId) || {
        content: '',
        metadata: firstToken.metadata,
        source: firstToken.source,
        created_at: firstToken.created_at,
        isStreaming: true,
        id: messageId
      };
      existing.content += combinedContent;
      newMap.set(messageId, existing);
      return newMap;
    });
    tokenBufferRef.current.set(messageId, []);
  };

  /**
   * Handle streaming message tokens
   */
  const handleAgentMessageToken = rawData => {
    logDebug('Agent: Received streaming token', rawData);
    let data;
    try {
      data = typeof rawData === 'string' ? JSON.parse(rawData) : rawData;
      logDebug('[Token]: Parsed token data:', data);
    } catch (error) {
      if (showConsoleMessagesRef?.current) {
        console.error('[Token]: Failed to parse token data:', error);
      }
      return;
    }
    logDebug('[Token]: Token type check:', data?.type, 'Expected: ModelClientStreamingChunkEvent');
    logDebug('[Token]: Type match:', data?.type === 'ModelClientStreamingChunkEvent');
    if (data && isStreamingChunkEvent(data) && data.source !== 'user') {
      logDebug('[Token]: Processing streaming token for message:', data.full_message_id);
      const messageId = data.full_message_id;

      // Initialize buffer if needed
      if (!tokenBufferRef.current.has(messageId)) {
        tokenBufferRef.current.set(messageId, []);
      }

      // Add token to buffer
      tokenBufferRef.current.get(messageId).push({
        content: data.content,
        metadata: data.metadata,
        source: data.source,
        created_at: data.created_at
      });

      // Clear existing timer
      if (bufferTimerRef.current.has(messageId)) {
        clearTimeout(bufferTimerRef.current.get(messageId));
      }

      // Flush immediately if buffer reaches 5 tokens, otherwise set timer
      const currentBufferSize = tokenBufferRef.current.get(messageId).length;
      if (currentBufferSize >= 5) {
        flushTokenBuffer(messageId);
      } else {
        const timer = setTimeout(() => {
          flushTokenBuffer(messageId);
          bufferTimerRef.current.delete(messageId);
        }, 50);
        bufferTimerRef.current.set(messageId, timer);
      }
    } else {
      logDebug('[Token]: Token did NOT match expected criteria - skipping');
      logDebug('[Token]: Actual - type:', data?.type, 'source:', data?.source);
      logDebug('[Token]: Expected: type="ModelClientStreamingChunkEvent" and source != "user"');
    }
  };
  return {
    handleAgentMessage,
    handleAgentActionMessage,
    handleAgentTyping,
    handleAgentError,
    handleUserInputRequest,
    handleAgentMessageToken
  };
};